/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { HotelIntegrations } from './types';

/** Integrations required by guest / confirmation / e-invoice flows. */
export const BOOKING_FLOW_HOTEL_INTEGRATIONS = [
  'ContactDetailsCard',
  'FareSummaryCard',
  'IdleTimeoutModal',
  'useMalaysianEInvoice',
] as const satisfies ReadonlyArray<keyof HotelIntegrations>;

/**
 * Recommended set for Super App embed and full hotel hosts.
 * `useNearestAirports` is optional at runtime (falls back to HotelConfig /
 * defaults) but should still be provided when Flights geo is available.
 */
export const RECOMMENDED_HOTEL_INTEGRATIONS = [
  ...BOOKING_FLOW_HOTEL_INTEGRATIONS,
  'useNearestAirports',
] as const satisfies ReadonlyArray<keyof HotelIntegrations>;

export const getMissingHotelIntegrations = (
  integrations: Readonly<Partial<HotelIntegrations>>,
  required: ReadonlyArray<
    keyof HotelIntegrations
  > = RECOMMENDED_HOTEL_INTEGRATIONS,
): Array<keyof HotelIntegrations> =>
  required.filter((key) => integrations[key] == null);

export const warnMissingHotelIntegrations = (
  integrations: Readonly<Partial<HotelIntegrations>>,
  context = 'HotelIntegrationsProvider',
): void => {
  if (typeof __DEV__ === 'undefined' || !__DEV__) {
    return;
  }

  const missing = getMissingHotelIntegrations(integrations);
  if (missing.length === 0) {
    return;
  }

  console.warn(
    `[${context}] Missing hotel integrations: ${missing.join(', ')}. ` +
      'Pass them via HotelIntegrationsProvider or the Hotels `integrations` prop. ' +
      'See packages/hotels/README.md (Super App integration).',
  );
};
